import { ValidatorFn, Validators } from '@angular/forms';
import { FieldValidationsFactory } from '../validator/field-validations.factory';
import { ValidationsFactory } from '../validator/validations-factory';
import { ColumnOptions, RuleAssociation } from './column';
import { Data } from './data';
import { HasValidatorFn } from './has-validator-fn';

/**
 * Implements HasValidatorFn for Column objects, it returns an array of angular ValidatorFn's
 * @returns
 */
export function hasColumnValidatorFn(): HasValidatorFn {
  function makeFieldValidations({ fieldValidations = [] }: ColumnOptions): ValidatorFn[] {
    return fieldValidations.map(FieldValidationsFactory);
  }

  function makeValidations({ validations = [] }: ColumnOptions): ValidatorFn[] {
    return validations.map(ValidationsFactory);
  }

  function makeValidators(options: ColumnOptions): ValidatorFn[] {
    return [...makeValidations(options), ...makeFieldValidations(options)].filter(Boolean);
  }

  function validatorFns() {
    const { options } = this;
    const validators = options ? makeValidators(options) : [];
    if (this.required) {
      validators.push(Validators.required);
    }
    if (this.formControlOptions != null && this.formControlOptions.validatorFn != null) {
      const controlValidations = Array.isArray(this.formControlOptions.validatorFn)
        ? this.formControlOptions.validatorFn
        : [this.formControlOptions.validatorFn];
      controlValidations.forEach(v => validators.push(v));
    }
    return validators;
  }
  return { validatorFns };
}

/**
 * Colloquial behaviors
 */
export interface HasColloquial {
  /**
   * Returns whether a column is considered "colloquial" or not
   */
  isColloquial(): boolean;
}

/**
 * Composition function to add HasColloquial behavior
 * @returns
 */
export function hasColloquial(): HasColloquial {
  function isColloquial() {
    return this.field.includes('colloquial/');
  }
  return { isColloquial };
}

export interface HasGetReadPath {
  /**
   * return path instead of field, if path is not empty
   */
  getReadPath(_state?: Data): string;
}

/**
 * Composition method extend getReadPath function to given object
 */
export function hasReadPath(_state?: Data): HasGetReadPath {
  function getReadPath() {
    return this.path || this.field;
  }
  return { getReadPath };
}

/**
 * Available column relationships
 */
export enum RelatedFieldRelationship {
  UOM = 'UOM',
  Colloquial = 'Colloquial',
  SearchGroup = 'Search Group',
  FilterGroup = 'Filter Groups',
  Cascade = 'Cascade',
  EstimatedF = 'EstimatedF',
  InputGroup = 'Input Group',
}

export interface HasRelationships {
  /**
   * returns all the declared relationships a given column may have by mapping it with RelatedFieldRelationship
   * { UOM: 234, Colloquial: 235, SearchGroup: 233}
   */
  getRelations: () => { [key: string]: number };
  /**
   * It returns whether a column has a column relation of a given key
   */
  hasRelationType: (key: string) => boolean;
  /**
   * General purpose returns the associated Column objects
   */
  getRelatedColumns?: (key?: string) => this[];
}

/**
 * Method used to extend and map relationships to given column
 */
export function hasRelationships(): HasRelationships {
  function getRelations() {
    const enumMapping = Object.assign({}, ...Object.entries(RelatedFieldRelationship).map(([a, b]) => ({ [b]: a })));
    const relations = this.options?.relatedFields ?? [];
    return relations.reduce((acc, rel) => {
      const relKey = enumMapping[rel.relationship] ?? rel.relationship;
      return { ...acc, [`${relKey}`]: rel.columnId };
    }, {});
  }

  function hasRelationType(key: RelatedFieldRelationship | string): boolean {
    return (this.related ?? []).some(relation => relation.relationType === key);
  }

  function getRelatedColumns(key?: RelatedFieldRelationship | string): HasRelationships[] {
    const related = this.related ?? [];
    return key == null ? related : related.filter(r => r.relationType === key);
  }

  return {
    getRelations,
    hasRelationType,
    getRelatedColumns,
  };
}

/**
 * A given object with rules associated
 */
export interface HasRuleAssociations {
  /**
   * Returns RuleAssociations or empty list
   */
  getRuleAssociations(): RuleAssociation[];
}

/**
 * Composite function used to extend and map relationships to given column
 */
export function hasRules(): HasRuleAssociations {
  function getRuleAssociations(): RuleAssociation[] {
    return this.options?.rules;
  }

  return { getRuleAssociations };
}

export interface HasEDPIngestionMethod {
  canCreate(): boolean;
  canUpdate(): boolean;
  canDelete(): boolean;
}

export function hasEDPIngestion(): HasEDPIngestionMethod {
  function canCreate() {
    return this.parent?.create != null;
  }

  function canUpdate() {
    return this.parent?.update != null;
  }

  function canDelete() {
    return this.parent?.delete != null;
  }
  return { canCreate, canUpdate, canDelete };
}
